// Shared Type Writer / Wavy Text wrappers — used by AtomicBoxPlugin
// (field-level via AnimatedField) and by VisorBox for atomic box-level
// animations (whose state.__text is JSON, so the legacy HTML-parsing path
// doesn't apply).

import React, { useRef } from "react";
import DOMPurify from "dompurify";
import { applyStyle, stagePxScale } from "./animationEngine.es6";
import { useAnimationSampler } from "./useAnimationSampler";
import { wavyStyleAt } from "./wavyAnimation";
import { getTypewriterClipPath } from "./typewriterClip";

/* eslint-disable react/prop-types */

// A detached element still runs handlers like <img onerror> when parsed.
const parseHtml = (html) => {
  const temp = document.createElement("div");
  temp.innerHTML = DOMPurify.sanitize(html, { ADD_ATTR: ["target"] });
  return temp;
};

export function extractText(children) {
  if (children == null || children === false) return null;
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) {
    const parts = children.map(extractText).filter(Boolean);
    return parts.length ? parts.join("") : null;
  }
  if (children.props) {
    const fromChildren = children.props.children;
    if (fromChildren != null) {
      const inner = extractText(fromChildren);
      if (inner) return inner;
    }
    if (typeof children.props.value === "string") return children.props.value;
  }
  return null;
}

export function getRootElement(children) {
  const arr = React.Children.toArray(children).filter(
    (c) => c != null && c !== false && typeof c !== "string" && typeof c !== "number"
  );
  if (arr.length === 1 && arr[0] && arr[0].type) return arr[0];
  return null;
}

// Replace the FIRST text-bearing leaf (string child OR EditableText-like
// element with a string `value` prop) while preserving every ancestor —
// so bullet markers, badge shells, and styled font containers survive
// and Wavy chars inherit the parent's font/color.
export function replaceTextLeaf(node, wavy) {
  if (node == null || node === false) return { node, replaced: false };
  if (typeof node === "string" || typeof node === "number") {
    return { node: wavy, replaced: true };
  }
  if (Array.isArray(node)) {
    let didReplace = false;
    const out = node.map((child) => {
      if (didReplace) return child;
      const r = replaceTextLeaf(child, wavy);
      if (r.replaced) didReplace = true;
      return r.node;
    });
    return { node: out, replaced: didReplace };
  }
  if (!node.props) return { node, replaced: false };
  if (typeof node.props.value === "string") {
    return { node: wavy, replaced: true };
  }
  if (node.props.children != null) {
    const r = replaceTextLeaf(node.props.children, wavy);
    if (r.replaced) {
      return { node: React.cloneElement(node, {}, r.node), replaced: true };
    }
  }
  return { node, replaced: false };
}

function countCharacters(node) {
  if (node == null || node === false) return 0;
  if (typeof node === "string" || typeof node === "number") return String(node).length;
  if (Array.isArray(node)) return node.reduce((sum, child) => sum + countCharacters(child), 0);
  if (!node.props) return 0;
  if (typeof node.props.value === "string") return node.props.value.length;
  if (node.props.dangerouslySetInnerHTML?.__html) {
    return parseHtml(node.props.dangerouslySetInnerHTML.__html).textContent.length;
  }
  return countCharacters(node.props.children);
}

function styleObject(style) {
  const out = {};
  for (let index = 0; index < style.length; index += 1) {
    const property = style[index];
    const camel = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    out[camel] = style.getPropertyValue(property);
  }
  return out;
}

function domAttributes(node) {
  const props = {};
  Array.from(node.attributes).forEach(({ name, value }) => {
    if (name === "style") props.style = styleObject(node.style);
    else if (name === "class") props.className = value;
    else if (name === "for") props.htmlFor = value;
    else props[name] = value;
  });
  return props;
}

function wavyCharacters(text, state, animationKey) {
  return String(text)
    .split("")
    .map((letter) => {
      const index = state.index++;
      if (!letter.trim()) return letter;
      return (
        <span
          key={`${animationKey}-${index}`}
          data-aidea-wavy=""
          data-aidea-wavy-index={index}
          data-aidea-wavy-count={state.total}
          style={{ display: "inline-block" }}
        >
          {letter}
        </span>
      );
    });
}

function domToWavyReact(node, state, animationKey, path) {
  if (node.nodeType === Node.TEXT_NODE) {
    return wavyCharacters(node.textContent, state, `${animationKey}-${path}`);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const children = Array.from(node.childNodes).map((child, index) =>
    domToWavyReact(child, state, animationKey, `${path}-${index}`)
  );
  return React.createElement(
    node.tagName.toLowerCase(),
    { ...domAttributes(node), key: `${animationKey}-${path}` },
    ...children
  );
}

function transformWavyText(node, state, animationKey, path = "root") {
  if (node == null || node === false) return node;
  if (typeof node === "string" || typeof node === "number") {
    return wavyCharacters(node, state, `${animationKey}-${path}`);
  }
  if (Array.isArray(node)) {
    return node.map((child, index) =>
      transformWavyText(child, state, animationKey, `${path}-${index}`)
    );
  }
  if (!node.props) return node;

  if (typeof node.props.value === "string") {
    return React.cloneElement(node, {
      key: node.key ?? `${animationKey}-${path}`,
      value: <>{wavyCharacters(node.props.value, state, `${animationKey}-${path}-value`)}</>,
    });
  }

  const html = node.props.dangerouslySetInnerHTML?.__html;
  if (html) {
    const temp = parseHtml(html);
    const children = Array.from(temp.childNodes).map((child, index) =>
      domToWavyReact(child, state, animationKey, `${path}-html-${index}`)
    );
    const props = {
      ...node.props,
      key: node.key ?? `${animationKey}-${path}`,
      dangerouslySetInnerHTML: undefined,
    };
    return React.cloneElement(node, props, children);
  }

  if (node.props.children != null) {
    return React.cloneElement(
      node,
      { key: node.key ?? `${animationKey}-${path}` },
      transformWavyText(node.props.children, state, animationKey, `${path}-children`)
    );
  }
  return node;
}

function containsWavyWrapper(node) {
  if (node == null || typeof node === "string" || typeof node === "number") return false;
  if (Array.isArray(node)) return node.some(containsWavyWrapper);
  if (!node.props) return false;
  return node.type === WavyWrap || containsWavyWrapper(node.props.children);
}

// Line-aware clip (see typewriterClip.js) driven both from a wall-clock rAF
// tick and from seekTimeSec deterministically. Multi-line text reveals line
// by line; single-line falls back to a plain inset.
// `active`: keeps the wrapper mounted but idle (progress 0) until it flips
// true — lets exit wrappers stay in the tree without remounting children.
export function TypeWriterWrap({
  children,
  duration = 1,
  delay = 0,
  isPaused = false,
  seekTimeSec = null,
  timeOriginSec = null,
  sceneId = null,
  reverse = false,
  active = true,
  style = {},
}) {
  const ref = useRef(null);
  const dur = Math.max(0.001, duration);

  const setClipForProgress = (el, p) => {
    const reveal = reverse ? 1 - p : p;
    el.style.clipPath = getTypewriterClipPath(el, reveal);
  };

  useAnimationSampler(
    ref,
    (el, t) => {
      const p = Math.max(0, Math.min(1, (t - delay) / dur));
      setClipForProgress(el, p);
    },
    { seekTimeSec, timeOriginSec, sceneId, isPaused, active }
  );

  // <div display:inline-block> rather than <span> so block-level children
  // (Title / heading components) are HTML-valid and clipPath sizes to content.
  // No white-space override: text must wrap exactly as it does unanimated so
  // the line-aware clip measures the real rendered lines.
  return (
    <div
      ref={ref}
      style={{
        display: "inline-block",
        verticalAlign: "top",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function WavyWrap({
  children,
  duration = 2,
  delay = 0,
  isPaused = false,
  seekTimeSec = null,
  timeOriginSec = null,
  sceneId = null,
  reverse = false,
  active = true,
  style = {},
  animationKey = "wavy",
}) {
  const ref = useRef(null);
  const hasNestedWavy = containsWavyWrapper(children);
  const exitApplied = useRef(false);
  useAnimationSampler(
    ref,
    (el, t) => {
      if (reverse && (t < delay || (!active && seekTimeSec == null && timeOriginSec == null))) {
        // Undo an exit when seeking back. A nested Wavy entry samples these
        // same characters itself, so only reset characters owned by this exit.
        if (!hasNestedWavy && exitApplied.current) {
          el.querySelectorAll("[data-aidea-wavy]").forEach((char) => {
            char.style.opacity = "";
            char.style.transform = "";
          });
        }
        exitApplied.current = false;
        return;
      }
      if (reverse) exitApplied.current = true;
      const scale = stagePxScale(el);
      el.querySelectorAll("[data-aidea-wavy]").forEach((char) => {
        const index = Number(char.dataset.aideaWavyIndex || 0);
        const count = Math.max(1, Number(char.dataset.aideaWavyCount || 1));
        const start = delay + (index * duration * 0.7) / count;
        applyStyle(char, wavyStyleAt(t, start, duration * 0.3, reverse, scale));
      });
    },
    { seekTimeSec, timeOriginSec, sceneId, isPaused, active }
  );

  // Nested entry and exit wrappers animate the same character spans.
  const total = hasNestedWavy ? 0 : countCharacters(children);
  const renderedChildren = hasNestedWavy
    ? children
    : transformWavyText(children, { index: 0, total: Math.max(1, total) }, animationKey);

  return (
    <div ref={ref} style={{ display: "inline-block", verticalAlign: "top", ...style }}>
      {renderedChildren}
    </div>
  );
}

export const TYPE_WRITER_NAMES = new Set(["Type Writer", "Type Writer Out"]);
export const WAVY_NAMES = new Set(["Wavy Text", "Wavy Text Out"]);
